import Header from "./components/Header";
import Section from "./components/Section";
import InvestorRelations from "./pages/InvestorRelations";

const services = [
  {
    title: "Domain-Specific AI Systems",
    description:
      "RAG (retrieval-augmented generation) with private knowledge bases, evaluation, and governance.",
  },
  {
    title: "AI Agents for Operations",
    description:
      "Automate repeatable decisions: triage, reconciliation, monitoring, report generation, and workflow actions.",
  },
  {
    title: "AI Enablement & Production Hardening",
    description:
      "Security, compliance readiness, prompt/model evals, observability, and cost controls.",
  },
];

const serviceDetails = [
  "AI strategy & discovery (1-2 weeks)",
  "Data readiness & knowledge architecture",
  "RAG implementation (docs, DBs, internal tools)",
  "Agent design & orchestration (tool use, guardrails)",
  "Evaluation harness (accuracy, hallucination checks, regression tests)",
  "Deployment (cloud/on-prem options), monitoring, and iteration",
];

const engagementSteps = [
  {
    title: "Discovery",
    description: "Define outcomes, constraints, success metrics, and data realities.",
  },
  {
    title: "Pilot",
    description: "Build a narrow agent with measurable success criteria and governance.",
  },
  {
    title: "Production",
    description: "Scale the system with monitoring, security controls, and handover.",
  },
];

const useCases = [
  "Customer support knowledge agent",
  "Fraud / risk triage assistant",
  "Finance reconciliation & anomaly detection",
  "Sales proposal / RFP response agent",
  "Internal policy & compliance Q&A agent",
  "Ops monitoring + alerting agent",
];

const trustPoints = [
  "Outcome-driven delivery",
  "Security-first engineering",
  "Evaluation before rollout",
  "Human-in-the-loop controls",
  "Transparent cost and performance metrics",
];

const HomePage = () => (
  <main>
    <section id="top" className="hero">
      <div className="container hero__grid">
        <div>
          <div className="hero__eyebrow">GenaImpact AI Studio</div>
          <h1>
            Domain-Specific AI & <span className="accent-underline">AI Agents</span> — Built for Real
            Businesses.
          </h1>
          <p>
            We design and deploy AI systems grounded in your data, integrated into your workflows,
            and measured by outcomes.
          </p>
          <div className="hero__actions">
            <a className="btn btnPrimary" href="#contact">
              Book a Discovery Call
            </a>
            <a className="btn btnGhost" href="#services">
              See Services
            </a>
          </div>
        </div>
        <div className="hero__visual">
          <img src="/assets/gena-robot.png" alt="GenaImpact intelligence" />
          <div className="hero__stats">
            <div>
              <strong>Domain-specific AI</strong>
              <div>Grounded in your data</div>
            </div>
            <div>
              <strong>AI agents</strong>
              <div>Observe, decide, act, report</div>
            </div>
            <div>
              <strong>Production delivery</strong>
              <div>Evaluation, security, monitoring</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <Section
      id="what-we-do"
      title="What We Do"
      description="AI professional services for organizations ready to move from experimentation to production."
    >
      <div className="grid grid-3">
        {services.map((service) => (
          <article key={service.title} className="card">
            <div className="service__icon" aria-hidden="true" />
            <h3>{service.title}</h3>
            <p>{service.description}</p>
          </article>
        ))}
      </div>
    </Section>

    <Section
      id="services"
      title="Services"
      description="End-to-end delivery, from discovery through production and continuous improvement."
    >
      <ul className="services-list">
        {serviceDetails.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </Section>

    <Section
      id="approach"
      title="Engagement Model"
      description="A simple path to de-risk AI adoption and scale with confidence."
    >
      <ol className="stepper">
        {engagementSteps.map((step, index) => (
          <li key={step.title} className="step">
            <span>
              {index + 1}. {step.title}
            </span>
            <div>{step.description}</div>
          </li>
        ))}
      </ol>
    </Section>

    <Section
      id="use-cases"
      title="Use Cases"
      description="Examples of high-impact AI workflows we build for operations, finance, and support."
    >
      <div className="grid grid-3">
        {useCases.map((item) => (
          <article key={item} className="card">
            <h3>{item}</h3>
            <p>Designed for measurable outcomes and workflow integration.</p>
          </article>
        ))}
      </div>
    </Section>

    <Section
      id="about"
      title="Why GenaImpact"
      description="A process-first delivery partner focused on reliable outcomes."
    >
      <div className="about-grid">
        <ul className="about-list">
          {trustPoints.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
        <div>
          <p>
            We build AI systems that your teams can trust, maintain, and expand. Every engagement
            includes evaluation, governance, and the engineering needed to run safely in production.
          </p>
        </div>
      </div>
    </Section>

    <Section id="contact" title="Ready to build AI that delivers outcomes?">
      <div className="cta">
        <div>
          <p>Let's talk about your AI system, data, and desired outcomes.</p>
          <p>
            <a href="mailto:contact@genaimpact.com">contact@genaimpact.com</a>
          </p>
        </div>
        <div className="cta__actions">
          <a className="btn btnPrimary" href="mailto:contact@genaimpact.com">
            Book a Discovery Call
          </a>
        </div>
      </div>
    </Section>
  </main>
);

const Footer = () => (
  <footer className="footer">
    <div className="container">
      (c) 2025 GenaImpact · contact@genaimpact.com ·{" "}
      <a href="https://www.linkedin.com/in/jayper-sanchez-169678385" target="_blank" rel="noreferrer">
        LinkedIn
      </a>
    </div>
  </footer>
);

const App = () => {
  const isInvestorRelations =
    typeof window !== "undefined" && window.location.pathname === "/investor-relations";

  return (
    <>
      <Header />
      {isInvestorRelations ? <InvestorRelations /> : <HomePage />}
      <Footer />
    </>
  );
};

export default App;
